import * as React from "react";
import styled, { css } from "styled-components";
import Icon from "components/common/Icon";
import { getPrettifiedValue, convertValueToUnit } from "views/components/visualizations/utils/unitConverters";
import formatValueWithUnitLabel from "views/components/visualizations/utils/formatValueWithUnitLabel";
import getUnitTextLabel from "views/components/visualizations/utils/getUnitTextLabel";
import { formatTrend } from "util/NumberFormatting";
const background = (theme, trend = "neutral") => ({
  good: theme.colors.variant.success,
  bad: theme.colors.variant.danger,
  neutral: theme.colors.global.contentBackground
})[trend];
const Background = styled.div(({ theme, $trend }) => {
  const bgColor = background(theme, $trend);
  return css`
    text-align: right;
    ${$trend && css`
      background-color: ${bgColor} !important; /* Needed for report generation */
      color: ${theme.utils.contrastingColor(bgColor)} !important /* Needed for report generation */;
      color-adjust: exact !important; /* Needed for report generation */
    `}
  `;
});
const TextContainer = styled.div(({ theme, $trend }) => {
  const bgColor = background(theme, $trend);
  return css`
    margin: 5px;
    color: ${theme.utils.contrastingColor(bgColor)} !important /* Needed for report generation */;
    font-family: ${theme.fonts.family.body};
    color-adjust: exact !important; /* Needed for report generation */
  `;
});
const StyledIcon = styled(Icon)(({ theme, $trend }) => {
  const bgColor = background(theme, $trend);
  return css`
    path {
      fill: ${theme.utils.contrastingColor(bgColor)};
    }
  `;
});
const _trendDirection = (delta, trendPreference) => {
  switch (trendPreference) {
    case "LOWER":
      return delta > 0 ? "bad" : "good";
    case "HIGHER":
      return delta > 0 ? "good" : "bad";
    case "NEUTRAL":
    default:
      return "neutral";
  }
};
const _trendIcon = (delta) => (
  // eslint-disable-next-line no-nested-ternary
  delta === 0 ? "arrow_circle_right" : delta > 0 ? "arrow_circle_up" : "arrow_circle_down"
);
const diff = (current, previous) => {
  if (typeof current === "number" && typeof previous === "number") {
    const difference = current - previous;
    const differencePercent = difference / previous;
    return [difference, differencePercent];
  }
  return [NaN, NaN];
};
const getTrendConvertedValues = (current, previous, fieldUNit) => {
  const [difference, differencePercent] = diff(current, previous);
  if (!(fieldUNit == null ? void 0 : fieldUNit.isDefined)) {
    return {
      previousConverted: previous,
      differenceConverted: difference,
      differencePercent,
      unitAbbrevString: ""
    };
  }
  const originalParams = { unitType: fieldUNit == null ? void 0 : fieldUNit.unitType, abbrev: fieldUNit == null ? void 0 : fieldUNit.abbrev };
  const { unit: currentPrettyUnit } = getPrettifiedValue(current, originalParams);
  const currentPrettyParams = { unitType: currentPrettyUnit == null ? void 0 : currentPrettyUnit.unitType, abbrev: currentPrettyUnit == null ? void 0 : currentPrettyUnit.abbrev };
  const { value: prettyDiff } = convertValueToUnit(difference, originalParams, currentPrettyParams);
  const { value: previousPretty } = convertValueToUnit(previous, originalParams, currentPrettyParams);
  return {
    previousConverted: `${formatValueWithUnitLabel(previousPretty, currentPrettyUnit.abbrev)} (${previous})`,
    unitAbbrevString: ` ${getUnitTextLabel(currentPrettyUnit.abbrev)}`,
    differenceConverted: prettyDiff,
    differencePercent
  };
};
const Trend = React.forwardRef(
  ({ current, previous, trendPreference, unit = void 0 }, ref) => {
    const { differenceConverted, differencePercent, unitAbbrevString, previousConverted } = getTrendConvertedValues(
      current,
      previous,
      unit
    );
    const backgroundTrend = _trendDirection(differenceConverted, trendPreference);
    const trendIcon = _trendIcon(differenceConverted);
    const absoluteDifference = Number.isFinite(differenceConverted) ? `${formatTrend(differenceConverted)}${unitAbbrevString}` : "--";
    const relativeDifference = Number.isFinite(differencePercent) ? formatTrend(differencePercent, { percentage: true }) : "--";
    return /* @__PURE__ */ React.createElement(Background, { $trend: backgroundTrend, "data-testid": "trend-background" }, /* @__PURE__ */ React.createElement(TextContainer, { $trend: backgroundTrend, ref }, /* @__PURE__ */ React.createElement(StyledIcon, { name: trendIcon, $trend: backgroundTrend, "data-testid": "trend-icon" }), " ", /* @__PURE__ */ React.createElement("span", { "data-testid": "trend-value", title: `Previous value: ${previousConverted}` }, absoluteDifference, " / ", relativeDifference)));
  }
);
Trend.displayName = "Trend";
export default Trend;
